
import { Vue, Options, prop } from "vue-class-component";
import { OptionsClass } from "@/domain/entities/MainApp";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import {
  AwbValidationApiRequest,
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  BookingPayloadData,
  FlightData,
  FlightRequest
} from "@/domain/entities/CargoV2";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import CargoV2PlaneTrack from "./cargo-v2-plane-track.vue";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { debounce } from "lodash";
import { CourierManagementController } from "@/app/ui/controllers/CourierManagementController";
import { ApiRequestCourierManagementList } from "@/data/payload/api/CourierManagementApiRequest";
import { CourierManagementDetail } from "@/domain/entities/CourierManagement";
import InputVehicleNo from "../input-vehicle-no.vue";
import { dateTimePayload } from "@/app/infrastructures/misc/Utils";
import { buildShcOptions, isPlaneNogValid, toFlightDateTimeStr } from "../../utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CALCULATE_TYPE } from "../../modules/cargo-module";
import { DEFAULT_INTERNATIONAL_PRODUCT } from "../../modules/constants";

class Props {
  isEdit = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["update:form"],
  components: {
    CargoV2PlaneTrack,
    InputVehicleNo
  },
  watch: {
    "form.cargoType.value": {
      handler(prev: string, next: string) {
        if (prev == next) return;
        this.resetFilghtData();
      },
      deep: true
    }
  }
})
class CargoV2ManualInput extends Vue.with(Props) {
  iconCargoNumber = "";
  isOpenSelectCargoProduct = false;
  isOpenSelectShc = false;
  errorAwb = "";
  flightData = new FlightData();
  fetchDrivers: any = (search?: string) => search;
  nogTouched = false;

  mounted() {
    this.fetchDrivers = debounce((search: string) => {
      return this.fetchDriversInternal(search);
    }, 250);
    this.getCargoProductList();
    if (!this.form.nog?.trim()) {
      CargoV2Controller.onGenerateDefaultNog();
    }
  }

  get form() {
    return CargoV2Controller.form;
  }

  get isPlane() {
    return this.form.cargoType.value === "plane";
  }

  get isTruck() {
    return this.form.cargoType.value === "truck";
  }

  get isShip() {
    return this.form.cargoType.value === "ship";
  }

  get emptyTransport() {
    return this.form.cargoType.value === "";
  }

  get cargoNumberTooltip() {
    return this.isPlane
      ? this.$t("cargoV2.components.manualInput.cargoNumberTooltip")
      : "";
  }

  get isNogErrorVisible() {
    return this.isPlane && !isPlaneNogValid(this.form.cargoType.value, this.form.nog);
  }

  onChangeNog(value: string) {
    CargoV2Controller.updateNog(value);
  }

  onClear() {
    this.form.cargoNo = "";
    this.resetFilghtData();
  }

  onChangeCargoNumber(e: any) {
    this.form.cargoNo = e.target.value;
    this.resetFilghtData();
  }

  resetFilghtData() {
    CargoV2Controller.setForm({
      isValidAwbManual: false,
      isErroAwb: false,
      departureDate: "",
      arrivalDate: ""
    });

    this.errorAwb = "";
    this.iconCargoNumber = "";
    this.flightData = new FlightData();
  }

  async onCheckAwb() {
    this.resetFilghtData();
    try {
      MainAppController.showLoading();
      this.form.bookingPayload = new BookingPayloadData();

      const payload = new AwbValidationApiRequest({
        awbNo: this.form.cargoNo,
        originCity:
          AccountController.accountData.account_type_detail?.partnerLocation
            ?.city_code,
        destinationCity: this.form.destinationCityCode.code
      });
      const res = await CargoV2Controller.getAwbValidation(payload);
      this.iconCargoNumber = "check_circle";
      this.flightData = res.flightData;
      this.form.isValidAwbManual = true;
      this.form.bookingPayload = new BookingPayloadData({
        awbNo: this.form.cargoNo,
        shcCode: res.shc,
        flightRequest: res.flightData.itineraries.map(
          (item: FlightData) =>
            new FlightRequest({
              flightNo: item.flightNo,
              flightDateTime: toFlightDateTimeStr(item.departureDate),
              flightOrigin: item.originAirportCode,
              flightDestination: item.destinationAirportCode
            })
        )
      });
      this.form.originAirportCode = this.form.bookingPayload.flightRequest[0].flightOrigin;
      this.form.cargoVehicleNo = this.form.bookingPayload.flightRequest[0].flightNo;
      this.form.departureDate = dateTimePayload(res.flightData.departureDate);
      this.form.arrivalDate = dateTimePayload(res.flightData.arrivalDate);
      this.form.actualTotalCargoGrossweigth = res.awbDetails[0].grossWeight;
      this.form.actualTotalCargoVolumeweight = res.awbDetails[0].volumeWeight;
      this.form.cargoActualPiece = res.awbDetails[0].pieces;
      this.form.nog = (this.form.containsBosspackInDataTable && res.nog)
        ? `BOSSPACK-${res.nog}`
        : res.nog;
      this.form.shc = res.shc;

      const commodity = res.awbDetails[0].commodity;
      const commodityCode = commodity.split("-")[0];
      const commodityName = commodity.split("-")[1];

      this.form.cargoCommodity = new OptionsClass({
        name: commodityName,
        value: commodityCode,
        code: commodityCode
      });
      this.form.productCargo =
        res.awbDetails[0].product ||
        CargoV2Controller.cargoProductList?.[0]?.cargoProductTypeCode;
      CargoV2Controller.setForm(this.form);
      await this.calculateVolume(CALCULATE_TYPE.CHANGE_GW);
    } catch (err) {
      this.form.isErroAwb = true;
      this.iconCargoNumber = "close_circle";
      this.errorAwb = err.response
        ? AccountController.accountData.accountIsForeign
          ? err.response.data.message
          : err.response.data.message_id
        : this.$t("cargoV2.components.manualInput.checkInternet");
      this.form.isValidAwbManual = false;
    } finally {
      MainAppController.closeLoading();
    }
  }

  async calculateVolume(type: string) {
    await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: type,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: this.form.cargoType.value,
          chargeableWeightReweigh: this.form.cargoTotalEstimateChargeableWeight,
          currentGrossWeight: this.form.actualTotalCargoGrossweigth,
          height: this.form.actualDimension.cargoActualHeightDimension,
          length: this.form.actualDimension.cargoActualLengthDimension,
          width: this.form.actualDimension.cargoActualWidthDimension,
          totalPieces: this.form.cargoActualPiece,
          volumeWeightReweigh: this.form
            .totalAccumulationEstimationVolumeWeightDataTable
        })
      })
    );
    await this.$nextTick();
    await this.$nextTick();
    await this.$nextTick();
    CargoV2Controller.setForm(this.form);
  }

  get commodityOptions() {
    return CargoV2Controller.commodityOptions;
  }

  onSelectCommodity(val: any) {
    this.form.cargoCommodity = val;

    this.form.shc = val.shcCode;
    this.form.shcName = val.shcName;
    CargoV2Controller.setForm(this.form);
  }

  onOpenSelectCargoProduct() {
    this.isOpenSelectCargoProduct = true;
  }

  onCloseSelectCargoProduct() {
    this.isOpenSelectCargoProduct = false;
  }

  async getCargoProductList() {
    try {
      const cargoProductList = await CargoV2Controller.getCargoProductList(
        "active"
      );
      CargoV2Controller.setCargoProductList(cargoProductList);

      if (!CargoV2Controller.cargoProductList.length) return;

      const productFound = CargoV2Controller.cargoProductList.find(
        (product: any) =>
          product.cargoProductTypeCode === this.form.productCargo
      );
      if (!productFound) {
        if (AccountController.accountData.accountIsForeign) {
          CargoV2Controller.updateSelectedCargoProduct(
            DEFAULT_INTERNATIONAL_PRODUCT
          );
          return;
        }
        CargoV2Controller.updateSelectedCargoProduct(
          CargoV2Controller.cargoProductList[0].cargoProductTypeCode
        );
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("cargoV2.components.common.errorGetData"),
          this.getCargoProductList
        )
      );
    }
  }

  get listProductCargo() {
    const defaultChoices: any = [
      {
        name: this.$t("cargoV2.components.manualInput.selectProduct"),
        value: "",
        status: ""
      }
    ];
    const cargos = CargoV2Controller.cargoProductList
      .map((e: any) => ({
        name: e.cargoProductTypeCode,
        value: e.cargoProductTypeCode,
        status: e.cargoProductTypeStatus
      }))
      .filter(
        (e: any) =>
          AccountController.accountData.accountIsForeign ||
          this.form.destinationCityCode.countryCode !== "ID" ||
          e.value !== DEFAULT_INTERNATIONAL_PRODUCT
      );

    return defaultChoices.concat(cargos);
  }

  onSelectCargoProduct(_: string, value: string) {
    this.form.productCargo = value;
    this.onCloseSelectCargoProduct();
    CargoV2Controller.setForm(this.form);
  }

  onSelectShc(value: any) {
    this.form.shc = value.value;
    CargoV2Controller.setForm(this.form);
  }

  get listShc() {
    return buildShcOptions(
      CargoV2Controller.commodityOptions,
      this.form.cargoCommodity
    );
  }

  get shcItem() {
    return new OptionsClass({
      name: this.form.shc || "-",
      value: this.form.shc
    });
  }

  // driver
  loadingDriver = true;
  listVehicleNumber: OptionsClass[] = [];

  get driverItem() {
    return this.form.driverSelected === "other"
      ? new OptionsClass({
          label: this.$t("cargoV2.components.common.other"),
          value: "other"
        })
      : new OptionsClass({
          label: this.form.cargoVehicleNo
            ? `${this.form.cargoVehicleNo} - ${this.form.driverSelected}`
            : "",
          name: this.form.driverSelected,
          value: this.form.cargoVehicleNo
        });
  }

  async fetchDriversInternal(search: any) {
    const keyword = typeof search === "string" ? search : "";
    if (!keyword.length || keyword.length > 2) {
      try {
        this.loadingDriver = true;
        this.listVehicleNumber = [];

        const resp = await CourierManagementController.getList(
          new ApiRequestCourierManagementList({
            q: keyword,
            courierStatus: { name: "ACTIVE", value: "ACTIVE" }
          })
        );
        const data = resp.data.map(
          (item: CourierManagementDetail) =>
            new OptionsClass({
              label: `${item.vehiclePlate} - ${item.fullname}`,
              name: item.fullname,
              value: item.vehiclePlate,
              id: item.courierId
            })
        );

        this.listVehicleNumber = [
          new OptionsClass({
            label: this.$t("cargoV2.components.common.other"),
            value: "other"
          }),
          ...data
        ];
      } catch (err) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            err,
            this.$t("cargoV2.components.manualInput.errorGetCourierData"),
            () => this.fetchDriversInternal("")
          )
        );
      } finally {
        this.loadingDriver = false;
      }
    }
  }

  onSelectDriver(val: any) {
    if (val.value === "other") {
      this.form.isValidVehicleNo = false;
      this.form.cargoVehicleNo = "";
      this.form.driverSelected = val.value;
      CargoV2Controller.setForm(this.form);
      return;
    }

    if (val === "") {
      this.form.isValidVehicleNo = false;
      this.form.cargoVehicleNo = "";
      this.form.driverSelected = "";
      CargoV2Controller.setForm(this.form);
      return;
    }

    this.form.cargoVehicleNo = val.value;
    this.form.driverSelected = val.name;
    this.form.isValidVehicleNo = true;
    CargoV2Controller.setForm(this.form);
  }
}
export default CargoV2ManualInput;
